<template>
	<LiteDialogPanel class="recurrence-schedule-panel">
		<template #title>{{ recurrenceId ? translate( `editTitle`) : translate( `title` ) }}</template>
		<template #body>
			<div class="form">
				<LabeledInputField
					name="nameField"
					:blur-is-input="false"
					:debounce-input-duration="100"
					:is-valid="!errors.nameField"
					:validation-error="errorFor( `nameField` )"
					:value="nameField"
					:label="translate( `meetingNameLabel` )"
					@input="onNameInput"
				/>
				<LabeledInputField
					name="linkField"
					:blur-is-input="false"
					:debounce-input-duration="100"
					:is-valid="!errors.linkField"
					:left-icon="[ `fas`, `message-bot` ]"
					:validation-error="errorFor( `linkField` )"
					:value="linkField"
					:label="translate( `meetingLinkLabel` )"
					:hint="translate( `meetingLinkHint` )"
					@input="onLinkInput"
					@paste="onLinkPaste"
				/>
				<LabeledDropDown
					name="meetingTemplateField"
					:label="translate( `meetingTemplateLabel` )"
					:hint="translate( `meetingTemplateHint` )"
					:placeholder="translate( `meetingTemplatePlaceholder` )"
					:value="meetingTemplateField"
					:options="templateOptions"
					@change="onMeetingTemplateChange"
				>
					<template v-if="templateOptions.length === 1" v-slot:options>
						<span>This team/1:1 does not have any custom templates defined.</span>
					</template>
				</LabeledDropDown>
				<LabeledDropDown
					name="frequencyField"
					:is-valid="!errors.frequencyField"
					:options="frequencyOptions"
					:validation-error="errorFor( `frequencyField` )"
					:value="frequencyField"
					:label="translate( `meetingFrequencyLabel` )"
					@change="onFrequencyChange"
				/>
				<RecurrenceOffsetPickerField
					v-if="allowDayOffset"
					class="day-offset-picker"
					name="dayOffsetField"
					:day-options="longDayOptions"
					:day-value="daysField[ 0 ]"
					:offset-options="offsetOptions"
					:offset-value="dayOffsetField"
					:label="translate( `dayOffsetLabel` )"
					:is-valid="!errors.dayOffsetField"
					:validation-error="errorFor( `dayOffsetField` )"
					@change="onDayOffsetSelected"
				/>
				<RecurrenceDayPickerField
					v-else
					class="day-picker"
					name="daysField"
					:day-options="shortDayOptions"
					:hint="translate( `daysHint` )"
					:is-valid="!errors.daysField"
					:label="translate( `daysLabel` )"
					:multi-select="allowMultiDaySelection"
					:validation-error="errorFor( `daysField` )"
					:value="daysField"
					@select="onDaysSelected"
				/>
				<RecurrenceTimePickerField
					:errors="errors"
					:form-id="formId"
					:label="translate( `timezoneLabel` )"
					:meridiem-field="meridiemField"
					:time-field="timeField"
					:times="times"
					:timezone-field="timezoneField"
					:timezones="timezones"
					@formChange="onTimePickerChange"
				/>
				<LabeledCalendarPicker
					name="startDateField"
					:disabled-days="disabledStartDays"
					:filter-criteria="filteredStartDay"
					:is-valid="!errors.startDateField"
					:key="revision"
					:label="translate( `startDateLabel` )"
					:min-date="minDate"
					:timezone="timezoneField"
					:validation-error="errorFor( `startDateField` )"
					:value="jsStartDate"
					@change="onStartDateChange"
				/>
			</div>
		</template>
		<template #footer="{ onClose }">
			<div class="sub-footer">
				<div class="sub-footer--left">
					<TeamingButton2
						:disabled="busy"
						size="SMALL"
						@click="$emit( `save` )"
					>
						{{ needsZoomAuthorization ? translate(`authorizeAndSaveButton`) : translate( `saveButton` ) }}
					</TeamingButton2>
					<TeamingButton2
						:disabled="busy"
						size="SMALL"
						type="TERTIARY"
						@click="onClose"
					>
						{{ translate( `cancelButton` ) }}
					</TeamingButton2>
				</div>
				<div class="sub-footer--right">
					<TeamingButton2
						v-if="recurrenceId"
						:destructive="true"
						:disabled="busy"
						size="SMALL"
						@click="$emit( `delete`, { activeTab, recurrenceId } )"
					>
						{{ translate( `deleteButton` ) }}
					</TeamingButton2>
				</div>
			</div>
		</template>
	</LiteDialogPanel>
</template>

<script>
import { mapGetters } from "vuex";
import { DateTime } from "luxon";

import {
	FormMixin,
	LabeledCalendarPicker,
	LabeledDropDown,
	LabeledInputField,
	LiteDialogPanel,
	TeamingButton2,
	TranslateMixin,
	makeTeamingDropDownOption
} from "@teaming/common-ui";

import { isZoomLink } from "@teaming/common-util";

import {
	default as RecurrenceDayPickerField,
	makeOption as makeDayPickerOption
} from "./RecurrenceDayPickerField.vue";

import {
	default as RecurrenceOffsetPickerField,
	makeOption as makeOffsetPickerOption
} from "./RecurrenceOffsetPickerField.vue";

import RecurrenceTimePickerField from "./RecurrenceTimePickerField.vue";
import { VirtualMeetingDomains } from "@teaming/common-team";
import { VirtualMeetingType } from "@teaming/common-enums";
import { SCHEDULED_MEETING_TABS } from "./RecurrenceListPanel.vue";

export default {
	name: "RecurrenceSchedulePanel",
	teamingPackage: "common-team",
	emits: [ "save", "delete", ],
	mixins: [
		FormMixin,
		TranslateMixin,
	],
	components: {
		LabeledCalendarPicker,
		LabeledDropDown,
		LabeledInputField,
		LiteDialogPanel,
		RecurrenceDayPickerField,
		RecurrenceOffsetPickerField,
		RecurrenceTimePickerField,
		TeamingButton2,
	},
	props: {
		allowDayOffset: {
			type: Boolean,
			required: true,
		},
		allowMultiDaySelection: {
			type: Boolean,
			required: true,
		},
		dayOffsetField: {
			type: Number,
			required: true,
		},
		dayOffsetFrequencies: {
			type: Array, // Array.<String>
			required: true,
		},
		dayOffsets: {
			type: Array, // Array.<Number>
			required: true,
		},
		days: {
			type: Array,
			required: true,
		},
		daysField: {
			type: Array, // Array.<String>
			required: true,
		},
		disabledStartDays: {
			type: Array, // Array.<Number>
			required: true,
		},
		filteredStartDay: {
			type: Object,
			required: true,
		},
		frequencies: {
			type: Array,
			required: true,
		},
		frequencyField: {
			type: String,
			required: true,
		},
		linkField: {
			type: String,
			required: true,
		},
		meetingTemplateField: {
			type: String,
			required: false,
		},
		meetingTemplateOptions: {
			type: Array, // Array<{ value: String, text: String }>
			required: true,
		},
		meridiemField: {
			type: String,
			required: true,
		},
		multiDayFrequencies: {
			type: Array, // Array.<String>,
			required: true,
		},
		nameField: {
			type: String,
			required: true,
		},
		recurrenceId: {
			type: String,
			required: true,
		},
		startDateField: {
			type: String,
			required: true,
		},
		timeField: {
			type: String,
			required: true,
		},
		times: {
			type: Array,
			required: true,
		},
		timezoneField: {
			type: String,
			required: true,
		},
		timezones: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			activeTab: SCHEDULED_MEETING_TABS.RECURRING,
			needsZoomAuthorization: false,
		};
	},
	computed: {
		...mapGetters( "TeamlikeStream", [
			"isZoomAuthorized",
		] ),
		/** @override */
		// eslint-disable-next-line vue/no-unused-properties
		form() {
			const {
				dayOffsetField,
				daysField,
				frequencyField,
				linkField,
				meetingTemplateField,
				meridiemField,
				nameField,
				startDateField,
				timeField,
				timezoneField,
			} = this;

			return {
				dayOffsetField,
				daysField,
				frequencyField,
				linkField,
				meetingTemplateField,
				meridiemField,
				nameField,
				startDateField,
				timeField,
				timezoneField,
			};
		},
		offsetOptions() {
			const { dayOffsets, translate, } = this;
			return dayOffsets.map( offset => makeOffsetPickerOption( {
				text: translate( `offset${ offset }OptionText` ),
				value: `${ offset }`,
			} ) );
		},
		templateOptions() {
			const noTemplateOption = {
				text: this.translate( "noTemplateOptionText" ),
				value: "",
			};
			return [ noTemplateOption, ...this.meetingTemplateOptions, ].map( makeTeamingDropDownOption );
		},
		frequencyOptions() {
			const { frequencies, translate, } = this;
			return frequencies.map( frequency => makeTeamingDropDownOption( {
				text: translate( `${ frequency }OptionText` ),
				value: frequency,
			} ) );
		},
		shortDayOptions() {
			const { days, } = this;
			return days.map( day => makeDayPickerOption( {
				text: this.translate( `${ day }ShortLabel` ),
				value: day,
			} ) );
		},
		longDayOptions() {
			const { days, } = this;
			return days.map( day => makeDayPickerOption( {
				text: this.translate( `${ day }LongLabel` ),
				value: day,
			} ) );
		},
		minDate() {
			return DateTime.now()
				.setZone( this.timezoneField )
				.toJSDate();
		},
		jsStartDate() {
			const { timezoneField: zone, } = this;
			return DateTime.fromFormat( this.startDateField, "yyyy-MM-dd", { zone, } )
				.toJSDate();
		},
	},
	methods: {
		onNameInput( e ) {
			this.raiseFormChange( {
				nameField: e.target.value,
			} );
		},
		onLinkInput( e ) {
			const { value: linkField, } = e.target;
			this.needsZoomAuthorization = isZoomLink( linkField ) && !this.isZoomAuthorized;
			this.raiseFormChange( {
				linkField,
			} );
		},
		onLinkPaste( e ) {
			const value = e.target.value;
			const googleMeetDomain = VirtualMeetingDomains[ VirtualMeetingType.GOOGLE_MEET ];
			if ( value.startsWith( googleMeetDomain ) ) {
				this.raiseFormChange( {
					linkField: `https://${ value }`,
				} );
			}
		},
		onMeetingTemplateChange( { value: meetingTemplateField, } ) {
			this.raiseFormChange( {
				meetingTemplateField,
			} );
		},
		onFrequencyChange( { value: frequencyField, } ) {
			const {
				dayOffsetFrequencies,
				multiDayFrequencies,
				frequencyField: oldFrequencyField,
			} = this;

			let { daysField, dayOffsetField, } = this;

			const isMultiDayFrequency = multiDayFrequencies.includes( frequencyField );
			const wasMultiDayFrequency = multiDayFrequencies.includes( oldFrequencyField );

			const isOffsetFrequency = dayOffsetFrequencies.includes( frequencyField );
			const wasOffsetFrequency = dayOffsetFrequencies.includes( oldFrequencyField );

			// reset days field if switching TO an offset frequency
			if ( isOffsetFrequency && !wasOffsetFrequency ) {
				dayOffsetField = 1;
				daysField = [ daysField.length ? daysField[ 0 ] : "monday", ];
			}

			// if this is no longer a multi-day frequency then just
			// reset the days field to the first day selected
			if ( wasMultiDayFrequency && !isMultiDayFrequency ) {
				daysField = [ daysField.length ? daysField[ 0 ] : "monday", ];
			}

			// if this isn't an offset frequency, reset the offset
			if ( !isOffsetFrequency ) {
				dayOffsetField = 0;
			}

			this.raiseFormChange( {
				frequencyField,
				daysField,
				dayOffsetField,
			} );
		},
		onDaysSelected( daysField ) {
			this.raiseFormChange( {
				daysField,
			} );
		},
		onTimePickerChange( { timeField, timezoneField, meridiemField, } ) {
			this.raiseFormChange( {
				timeField,
				timezoneField,
				meridiemField,
			} );
		},
		onStartDateChange( jsDate ) {
			this.raiseFormChange( {
				startDateField: DateTime.fromJSDate( jsDate )
					.setZone( this.timezoneField )
					.toFormat( "yyyy-MM-dd" ),
			} );
		},
		onDayOffsetSelected( { dayOffsetField, daysField, } ) {
			this.raiseFormChange( {
				dayOffsetField,
				daysField,
			} );
		},
	},
};
</script>

<style lang="scss" scoped>
.recurrence-schedule-panel .form {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	gap: 1rem;
}

.recurrence-schedule-panel .sub-footer {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 0.5rem;

	width: 100%;
}

.recurrence-schedule-panel .sub-footer--left,
.recurrence-schedule-panel .sub-footer--right {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.5rem;

	@include for-size('sm') {
		width: 100%;
	}
}

.recurrence-schedule-panel .sub-footer--right {
	justify-content: flex-end;
}
</style>
